// Motor de cotización: cuota semanal fija con IVA incluido, al centavo, igual que loan-offerings.
// Lo usan el simulador de planes, el wizard de ventas y cobranza. Puro, sin React.

/** IVA sobre intereses por defecto (obligatorio, M2 §3.3), cuando el plan no trae el suyo. */
export const INTEREST_VAT_RATE = 0.16;

/** Días del año del interés: el contrato cobra la tasa anual entre 360 por cada día sobre saldo insoluto. */
const INTEREST_YEAR_DAYS = 360;

/** Días que corre el interés de cada cuota semanal. */
const DAYS_PER_WEEK = 7;

export interface QuoteInput {
    /** Precio del producto (MXN). */
    price: number;
    /** Enganche como % entero (30 = 30%). */
    downPaymentPct: number;
    /** Plazo en semanas. */
    weeks: number;
    /** Tasa nominal anual como fracción (2.60 = 260%). */
    tna: number;
    /** Comisión de apertura como fracción (0.02 = 2%). Se financia junto al saldo. */
    openingFeePct?: number;
    /** IVA sobre intereses como fracción (0.16 = 16%). Sin él se usa `INTEREST_VAT_RATE`. */
    vatRate?: number;
}

export interface QuoteWeek {
    week: number;
    payment: number;
    principal: number;
    interest: number;
    vat: number;
    balance: number;
}

export interface QuoteResult {
    /** false si los insumos no permiten cotizar (precio/plazo/tasa inválidos). */
    valid: boolean;
    downPayment: number;
    financed: number;
    weeks: QuoteWeek[];
    totals: { payment: number; principal: number; interest: number; vat: number };
}

export function quoteWeekly(input: QuoteInput): QuoteResult {
    const downPayment = Math.round((input.price * input.downPaymentPct) / 100);
    return quoteWeeklyFromDownPayment({ ...input, downPayment });
}

/** Variante con enganche en MONTO absoluto (el cotizador del paso 06 trabaja en pesos exactos). */
export function quoteWeeklyFromDownPayment(input: {
    price: number;
    downPayment: number;
    weeks: number;
    tna: number;
    openingFeePct?: number;
    vatRate?: number;
}): QuoteResult {
    const downPayment = input.downPayment;
    const financed = input.price - downPayment;
    const empty = { payment: 0, principal: 0, interest: 0, vat: 0 };
    if (input.price <= 0 || financed <= 0 || input.weeks <= 0 || input.tna <= 0) {
        return { valid: false, downPayment, financed, weeks: [], totals: empty };
    }

    const vatRate = input.vatRate ?? INTEREST_VAT_RATE;
    const financedCents = Math.round(financed * 100);
    const principalCents = financedCents + Math.round(financedCents * (input.openingFeePct ?? 0));
    const r = (input.tna * DAYS_PER_WEEK) / INTEREST_YEAR_DAYS;
    const paymentCents = fixedPaymentCents(principalCents, r * (1 + vatRate), input.weeks);

    const weeks: QuoteWeek[] = [];
    const totals = { ...empty };
    let balance = principalCents;
    for (let week = 1; week <= input.weeks; week++) {
        const interest = Math.round(balance * r);
        const vat = Math.round(interest * vatRate);
        // La última cuota amortiza el saldo completo: absorbe el residuo del redondeo.
        const principal = week === input.weeks ? balance : paymentCents - interest - vat;
        const payment = principal + interest + vat;
        balance -= principal;
        totals.payment += payment;
        totals.principal += principal;
        totals.interest += interest;
        totals.vat += vat;
        weeks.push({
            week,
            payment: payment / 100,
            principal: principal / 100,
            interest: interest / 100,
            vat: vat / 100,
            balance: balance / 100,
        });
    }
    return {
        valid: true,
        downPayment,
        financed,
        weeks,
        totals: {
            payment: totals.payment / 100,
            principal: totals.principal / 100,
            interest: totals.interest / 100,
            vat: totals.vat / 100,
        },
    };
}

/** Cuota fija con IVA en centavos: francesa sobre la tasa semanal con IVA, `P·ri/(1−(1+ri)^−n)`. */
function fixedPaymentCents(principalCents: number, rateWithVat: number, weeks: number): number {
    if (rateWithVat === 0) return Math.round(principalCents / weeks);
    return Math.round((principalCents * rateWithVat) / (1 - Math.pow(1 + rateWithVat, -weeks)));
}
